import { useState, useEffect } from "react";
import PatientSidebar from "../../components/dashboard/PatientSidebar";
import Button from "../../components/ui/Button";
import LogoSection from "../../components/ui/LogoSection";
import StudyController from "@/network/controllers/study.controller";
import { useSession } from "@/hooks/useSession";
import ParticipantController from "@/network/controllers/participant.controller";
import { sha256 } from "@/utils/crypto";
import { v4 as uuidv4 } from "uuid";
import Modal from "@/components/ui/Modal";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

export default function PatientSignatureConsent({ onSignOut = () => {} }) {
  const [file, setFile] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStudyId, setSelectedStudyId] = useState(null);
  const navigate = useNavigate()

  const { user } = useSession();
  const location = useLocation();

  const [consentsList, setConsentsList] = useState([]);
  const [patientData, setPatientData] = useState(null);

  const paramsSearch = new URLSearchParams(location.search);
  const studyIdFromUrl =
    paramsSearch.get("studyId") || paramsSearch.get("study_id");

  const isAlreadyUploaded = !!patientData?.participant_consent_status;

  useEffect(() => {
    const fetchPatientDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const patientId = user?.userId;
        if (!patientId) {
          setError("Patient ID not found in session.");
          setLoading(false);
          return;
        }

        const response = await StudyController.getPatientConsentDetails(
          patientId
        );
        // response is now an array (one row per study)
        const list = Array.isArray(response)
          ? response
          : response
          ? [response]
          : [];

        setConsentsList(list);

        let selected = null;
        if (studyIdFromUrl) {
          selected = list.find((r) => r.study_id === studyIdFromUrl) || null;
        }
        if (!selected) {
          selected =
            list.find(
              (r) =>
                !r.consent_id || r.participant_consent_status !== "validated"
            ) ||
            list[0] ||
            null;
        }

        if (selected) {
          setSelectedStudyId(selected.study_id);
          setPatientData(selected);
          
        } else {
          setError("No study found for this patient.");
        }
      } catch (err) {
        console.error("Error fetching patient details:", err);
        setError(err.message || "An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    };

    fetchPatientDetails();
  }, [user, studyIdFromUrl]);

  useEffect(() => {
    if (!selectedStudyId || !consentsList.length) return;
    const selected = consentsList.find((c) => c.study_id === selectedStudyId);
    if (selected) setPatientData(selected);
  }, [selectedStudyId, consentsList]);

  useEffect(() => {
    console.log("Selected study ID:", selectedStudyId);
    console.log("Patient data:", patientData);
  }, [selectedStudyId, patientData]);


  useEffect(() => {
    if (!patientData) return;
  
    if (!patientData.consent_id) {
      navigate("/patient-consent-signature", { replace: true });
    } else if (patientData.participant_consent_status === "revoked") {
      navigate("/patient-qr-code-error", { replace: true });
    } else if (
      patientData.participant_consent_status !== "validated" ||
      patientData.investigator_consent_status !== "validated"
    ) {
      navigate("/patient-consent-signature", { replace: true });
    } else {
      navigate("/patient-qr-code", { replace: true, state: { patientData } });
    }
  }, [patientData, navigate]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file || !patientData) {
      alert("Missing file or patient data");
      return;
    }

    setError(null);

    try {
      const params = {
        id: uuidv4(),
        participant_id: patientData.participant_id,
        site_icf_id: patientData.site_icf_document_id,
        participant_file: file,
        investigator_id: patientData.investigator_id,
        study_id: patientData.study_id,
      };

      setIsFinalizing(true);

      const fileResponse = await ParticipantController.uploadPatientConsent({
        ...params,
      });
      if (fileResponse?.success === false) {
        setIsFinalizing(false);
        setError(fileResponse?.message || "Upload error");
        return;
      }

      params.participant_signature = fileResponse.signature;
      params.participant_file_hash = fileResponse.encryptedFile;
      params.id = fileResponse.consentId;

      const response = await ParticipantController.savePatientSignedConsent({
        ...params,
      });
      if (response?.success !== false) {
        // refresh selection to reflect new status
        const updated = {
          ...patientData,
          participant_consent_status: "pending",
          consent_id: params.id,
        };
        setPatientData(updated);
        alert("Document uploaded successfully.");
      } else {
        console.error("Upload failed:", response?.message);
        alert(`Upload failed: ${response?.message}`);
      }
    } catch (err) {
      console.error("Erreur pendant l'upload :", err);
      alert("Une erreur inattendue est survenue pendant l'upload.");
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleDownload = async () => {
    const protocolId = patientData?.site_icf_document_id;
    const studyId = patientData?.study_id;
    if (!protocolId || !studyId) {
      console.error("Missing data to display ICF", { protocolId, studyId });
      alert("Unable to display ICF: missing data.");
      return;
    }
    try {
      const displayResult = await StudyController.getProtocolDocumentAndDisplay(
        studyId,
        protocolId
      );
      if (!displayResult.success) {
        console.error("Failed to display ICF:", displayResult.message);
        alert(`Unable to display ICF: ${displayResult.message}`);
      }
    } catch (error) {
      console.error("Unexpected error displaying ICF:", error);
      alert("Unexpected error while displaying the document.");
    }
  };

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const currentPatientName =
    patientData?.patient_firstname && patientData?.patient_lastname
      ? `${patientData.patient_firstname} ${patientData.patient_lastname}`.trim()
      : "Patient Name";
  const currentInvestigatorName =
    patientData?.investigator_firstname && patientData?.investigator_lastname
      ? `${patientData.investigator_firstname} ${patientData.investigator_lastname}`.trim()
      : "Investigator Name";
  const currentInvestigatorLocation =
    patientData?.investigator_location || "Investigator Location";
  const currentStudyName = patientData?.study_name || "Study Name";

  if (isFinalizing) {
    return (
      <Modal
        isOpen={true}
        onClose={() => {}}
        title="Uploading your signed consent..."
        hideCloseButton
        size="md"
      >
        <div className="p-6 flex flex-col items-center gap-4">
          <svg
            className="animate-spin h-8 w-8 text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
          <p className="text-center">
            Please wait while your signed consent document is being securely
            uploaded and processed.
          </p>
        </div>
      </Modal>
    );
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p>Loading patient consent details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center text-red-500">
        <p>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen relative">
      {/* Mobile menu button */}
      <button
        onClick={toggleSidebar}
        className="md:hidden fixed top-4 left-4 z-50 bg-white p-2 rounded-md shadow-md"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      {/* Sidebar - hidden on mobile by default, shown when toggled */}
      <div
        className={`${
          showSidebar ? "block" : "hidden"
        } md:block fixed md:relative z-40 h-full`}
      >
        <PatientSidebar
          patientName={currentPatientName}
          onSignOut={onSignOut}
          showSignature={true}
          showQRCode={false}
        />
      </div>

      {/* Overlay to close sidebar on mobile */}
      {showSidebar && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setShowSidebar(false)}
        ></div>
      )}

      <div className="flex-1 overflow-auto bg-dashboard-bg">
        <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-8">
          {/* Dropdown Study Selector */}
          <div className="flex flex-col ml-4 md:ml-8 w-full md:w-auto mt-10">
            <label
              htmlFor="study-select"
              className="text-sm font-medium text-gray-600 mb-1"
            >
              Select a study
            </label>
            <select
              id="study-select"
              className=" text-gray-900 text-lg block w-full md:w-[300px] px-4 py-2 "
              value={selectedStudyId || ""}
              onChange={(e) => setSelectedStudyId(e.target.value)}
            >
              {consentsList.map((consent) => (
                <option key={consent.study_id} value={consent.study_id}>
                  {consent.study_name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <main className="p-4 md:p-6">
          <div className="bg-dashboard-bg1 p-4 md:p-6 rounded-xl">
            <div className="flex flex-col md:flex-row">
              {/* Content - full width on mobile, 2/3 on desktop */}
              <div className="w-full md:w-2/3 pr-0 md:pr-6">
                <h2 className="text-xl font-semibold mb-4">
                  Informed Consent Form signature
                </h2>
                {!isAlreadyUploaded ? (
                  <div className="space-y-6 text-justify">
                    <p>
                      You have received an invitation from{" "}
                      {currentInvestigatorName} at {currentInvestigatorLocation}{" "}
                      to participate in the clinical study {currentStudyName}.
                    </p>
                    <div>
                      <h3 className="font-medium mb-2">
                        What is the Informed Consent Form ?
                      </h3>
                      <p className="">
                        The Informed Consent Form (ICF) is a crucial document
                        that explains the details of the study, including its
                        purpose, potential benefits, risks, and your rights as a
                        participant. By signing this document, you confirm that
                        you have been informed about the study and agree to
                        participate voluntarily.
                      </p>
                    </div>
                    <div>
                      <h3 className="font-medium mb-2">How to sign the ICF?</h3>
                      <p className=" mb-4">
                        To proceed with your participation, please follow these
                        steps:
                      </p>
                      <div className="pl-2 md:pl-6 space-y-6 md:space-y-4 ">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
                          <span className="font-medium">
                            1. Download the document
                          </span>
                          <Button
                            onClick={handleDownload}
                            variant="addbutton"
                            className="sm:ml-2 w-full sm:w-auto"
                          >
                            Download
                          </Button>
                        </div>
                        <div>
                          <span className="font-medium">
                            2. Read it carefully to ensure you understand all
                            the information
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">
                            3. Sign the document following the provided
                            instructions
                          </span>
                          <div className="pl-4 md:pl-6 mt-2">
                            <div className="flex items-start">
                              <span className="mr-2">a.</span>
                              <span>
                                You may sign it manually (handwritten)
                              </span>
                            </div>
                            <div className="flex items-start">
                              <span className="mr-2">b.</span>
                              <span>You may sign it electronically</span>
                            </div>
                            <div className="text-sm italic pl-4">
                              Depending on your preference and the legal
                              requirements in your country.
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
                          <span className="font-medium">
                            4. Upload the signed document
                          </span>
                          <input
                            type="file"
                            id="document-upload"
                            className="hidden"
                            onChange={handleFileChange}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          />
                          <label
                            htmlFor="document-upload"
                            className="inline-block sm:ml-2 cursor-pointer w-full sm:w-auto"
                          >
                            <Button
                              variant="addbutton"
                              className="pointer-events-none w-full sm:w-auto"
                              type="button"
                            >
                              Upload
                            </Button>
                          </label>
                        </div>
                      </div>
                      {file && (
                        <div className="mt-4 flex justify-end">
                          <Button onClick={handleUpload} variant="addbutton">
                            Submit
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center space-y-4 mt-6 md:mt-10 py-4 md:py-8 px-4 md:px-14 mx-auto max-w-full md:max-w-[80%]">
                    <div className="mt-4 md:mt-10"></div>
                    <p className="mb-6 md:mb-10">
                      Your document has been successfully submitted. Your
                      continued participation in the clinical study will now be
                      reviewed.
                    </p>
                    <p className="mt-4 md:mt-6">
                      Once validated, you will receive a QR code by mail.
                    </p>
                    <p className="mt-4 md:mt-6">
                      Please keep it safe and present it during your visits to
                      the study center.
                    </p>
                    <p className="mt-4 md:mt-6">
                      You may now close this window.
                    </p>
                  </div>
                )}
              </div>
              {/* Logo section component */}
              <LogoSection />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}